import api from '../../utils/api';

export type BlogStatus = 'draft' | 'published';
export type BlogAuthorType = 'doctor' | 'blog_writer' | 'writer' | 'admin';

export type CreateOrUpdateBlogInput = {
  title: string;
  content: string; // stored as Quill Delta JSON string (same as Flutter), or plain text fallback
  categoryId: string;
  status?: BlogStatus;
  blogDate?: string; // ISO string or datetime-local string
  tags?: string[] | null;
  referenceLinks?: string[] | null;
  authorId: string;
  authorType: BlogAuthorType;
  featuredImageFile?: File | null;
  featuredImageUrl?: string | null;
  // SEO Meta Package
  metaTitle?: string | null;
  metaDescription?: string | null;
  slug?: string | null;
  focusKeyword?: string | null;
  secondaryKeywords?: string | null;
  recommendedSchema?: string | null;
};

/** Let the browser set multipart boundary — a bare Content-Type breaks file uploads. */
const stripMultipartContentType = (data: FormData, headers: any) => {
  if (headers) {
    if (typeof headers.delete === 'function') {
      headers.delete('Content-Type');
    } else {
      delete headers['Content-Type'];
      delete headers['content-type'];
    }
  }
  return data;
};

const appendOptional = (fd: FormData, key: string, value?: string | null) => {
  if (value === undefined || value === null) return;
  fd.append(key, value);
};

const toFormData = (input: CreateOrUpdateBlogInput) => {
  const fd = new FormData();
  fd.append('title', input.title);
  fd.append('content', input.content);
  fd.append('categoryId', input.categoryId);
  fd.append('authorId', input.authorId);
  fd.append('authorType', input.authorType);

  if (input.status) fd.append('status', input.status);
  if (input.blogDate) fd.append('blogDate', input.blogDate);
  if (input.tags !== undefined) fd.append('tags', JSON.stringify(input.tags));
  if (input.referenceLinks !== undefined) fd.append('referenceLinks', JSON.stringify(input.referenceLinks));
  // Prefer file upload over URL so multer receives `req.file` on update
  if (input.featuredImageFile) {
    fd.append('featuredImage', input.featuredImageFile);
  } else if (input.featuredImageUrl) {
    fd.append('featuredImage', input.featuredImageUrl);
  }

  appendOptional(fd, 'metaTitle', input.metaTitle);
  appendOptional(fd, 'metaDescription', input.metaDescription);
  appendOptional(fd, 'slug', input.slug);
  appendOptional(fd, 'focusKeyword', input.focusKeyword);
  appendOptional(fd, 'secondaryKeywords', input.secondaryKeywords);
  appendOptional(fd, 'recommendedSchema', input.recommendedSchema);

  return fd;
};

export const createBlog = async (input: CreateOrUpdateBlogInput) => {
  const formData = toFormData(input);
  return api.post('/blogs/post', formData, {
    transformRequest: [(data, headers) => stripMultipartContentType(data as FormData, headers)],
  });
};

export const updateBlog = async (blogId: string, input: CreateOrUpdateBlogInput) => {
  const formData = toFormData(input);
  return api.put(`/blogs/update/${blogId}`, formData, {
    transformRequest: [(data, headers) => stripMultipartContentType(data as FormData, headers)],
  });
};

export const deleteBlog = async (blogId: string) => {
  return api.delete(`/blogs/delete/${blogId}`);
};

export const publishBlog = async (blogId: string) => {
  return api.patch(`/blogs/publish/${blogId}`);
};

export const getBlogsByAuthor = async (authorId: string) => {
  const response = await api.get(`/blogs/author/${authorId}`);
  const blogs = response.data?.blogs;
  return Array.isArray(blogs) ? blogs : [];
};

export const getBlogById = async (blogId: string) => {
  const response = await api.get(`/blogs/getByBlogId/${blogId}`);
  return response.data;
};
